import React, { memo, useContext, useState } from "react";
import { Box, Typography } from "@mui/material";
import { AppContext } from "../../AppContextProvider";
import { updateRfq } from "../../api/ProcurementClient";
import {
  getRFQStatusLabel,
  getRfqStatusColor,
  RFQStatus,
} from "../../models/RFQStatus";
import RfqStatusAutocomplete from "./RfqStatusAutocomplete";
import RfqStatusSinceTooltip from "./RfqStatusSinceTooltip";
import { bumpRfqClientEpoch, mergeRfqIntoList } from "../../utils/mergeRfqIntoList";

interface RfqStatusCellProps {
  rfqId: number;
  status: RFQStatus;
  /** Display label from the row — avoids Autocomplete until edit. */
  statusLabel?: string;
}

const RfqStatusCell: React.FC<RfqStatusCellProps> = ({
  rfqId,
  status,
  statusLabel,
}) => {
  const { rfqStatuses = [], setRfqs, setSnackbarErrorMsg } =
    useContext(AppContext);
  const [editing, setEditing] = useState(false);
  const colors = getRfqStatusColor(status, rfqStatuses);
  const label = statusLabel || getRFQStatusLabel(status, rfqStatuses);

  const handleStatusChange = async (newStatus: RFQStatus) => {
    if (newStatus === status) {
      setEditing(false);
      return;
    }
    bumpRfqClientEpoch(rfqId, { statusEdit: true });
    setRfqs((prev) =>
      prev.map((q) => (q.id === rfqId ? { ...q, status: newStatus } : q))
    );
    setEditing(false);
    try {
      const updatedRfq = await updateRfq(rfqId, { status: newStatus });
      bumpRfqClientEpoch(rfqId, { statusEdit: true });
      setRfqs((prev) => mergeRfqIntoList(prev, updatedRfq));
    } catch (error) {
      bumpRfqClientEpoch(rfqId, { statusEdit: true });
      setRfqs((prev) =>
        prev.map((q) => (q.id === rfqId ? { ...q, status } : q))
      );
      setSnackbarErrorMsg?.(
        error instanceof Error ? error.message : "Failed to update status"
      );
    }
  };

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      {editing ? (
        <RfqStatusAutocomplete
          status={status}
          hideCaption
          skipConfirm
          autoFocus
          onStatusChange={handleStatusChange}
          sx={{ width: "100%" }}
          onBlurExit={() => setEditing(false)}
        />
      ) : (
        <RfqStatusSinceTooltip rfqId={rfqId} status={status}>
          <Typography
            component="button"
            type="button"
            onClick={() => setEditing(true)}
            sx={{
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bg,
              color: colors.fg,
              borderRadius: 1,
              padding: "0 4px",
              margin: 0,
              font: "inherit",
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.6,
            }}
          >
            {label}
          </Typography>
        </RfqStatusSinceTooltip>
      )}
    </Box>
  );
};

export default memo(RfqStatusCell);
